// ============================================================================
// IssueMap Helpers
// ============================================================================

import * as turf from "@turf/turf";
import type { Job } from "../../../../../../../models";
import type { Polygon as GeoJSONPolygon, MultiPolygon, Position } from "geojson";

/**
 * Gets the boundary feature from a job, checking all possible locations
 */
export function getJobBoundaryGeometry(job: Job): GeoJSONPolygon | MultiPolygon | null {
	// Check files.boundaryGeoJSON first (primary location)
	const boundaryFromFiles = job.files?.boundaryGeoJSON;
	if (boundaryFromFiles?.features?.[0]?.geometry) {
		const geom = boundaryFromFiles.features[0].geometry;
		if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
			return geom as GeoJSONPolygon | MultiPolygon;
		}
	}

	// Check fileOptions.boundaryGeoJSON (alternative location)
	const boundaryFromFileOptions = job.fileOptions?.boundaryGeoJSON;
	if (boundaryFromFileOptions?.features?.[0]?.geometry) {
		const geom = boundaryFromFileOptions.features[0].geometry;
		if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
			return geom as GeoJSONPolygon | MultiPolygon;
		}
	}

	return null;
}

/**
 * Convert GeoJSON coordinates to Google Maps LatLngLiteral array
 */
export function convertCoordinatesToPath(coordinates: Position[]): google.maps.LatLngLiteral[] {
	return coordinates.map((coord) => ({
		lat: coord[1],
		lng: coord[0],
	}));
}

/**
 * Extract paths from a GeoJSON Polygon or MultiPolygon geometry
 */
export function extractPathsFromGeometry(geometry: GeoJSONPolygon | MultiPolygon): google.maps.LatLngLiteral[][] {
	if (geometry.type === "Polygon") {
		// For Polygon, return the outer ring (first coordinate array)
		return [convertCoordinatesToPath(geometry.coordinates[0])];
	} else if (geometry.type === "MultiPolygon") {
		// For MultiPolygon, return the outer ring of each polygon
		return geometry.coordinates.map((polygon) => convertCoordinatesToPath(polygon[0]));
	}
	return [];
}

/**
 * Generate parallel hatch lines clipped to a polygon or multipolygon.
 * Returns an array of line segments as LatLngLiteral arrays.
 */
export function generateHatchLines(
	geometry: GeoJSONPolygon | MultiPolygon,
	angleDegrees: number,
	spacingMeters: number,
): google.maps.LatLngLiteral[][] {
	const feature = turf.feature(geometry);
	const bbox = turf.bbox(feature);
	const bboxCenter = turf.center(feature);
	const diagonal = turf.distance(turf.point([bbox[0], bbox[1]]), turf.point([bbox[2], bbox[3]]), {
		units: "meters",
	});

	if (diagonal < spacingMeters) return [];

	const halfDiag = diagonal / 2;
	const results: google.maps.LatLngLiteral[][] = [];

	for (let offset = -halfDiag; offset <= halfDiag; offset += spacingMeters) {
		const perpAngle = angleDegrees + 90;
		const offsetPoint = turf.destination(bboxCenter, offset / 1000, perpAngle, { units: "kilometers" });

		const lineStart = turf.destination(offsetPoint, halfDiag / 1000, angleDegrees, { units: "kilometers" });
		const lineEnd = turf.destination(offsetPoint, halfDiag / 1000, angleDegrees + 180, { units: "kilometers" });

		const line = turf.lineString([turf.getCoord(lineStart), turf.getCoord(lineEnd)]);

		try {
			const split = turf.lineSplit(line, feature);

			for (const segment of split.features) {
				const coords = segment.geometry.coordinates;
				const mid = turf.midpoint(turf.point(coords[0]), turf.point(coords[coords.length - 1]));
				if (turf.booleanPointInPolygon(mid, feature)) {
					results.push(coords.map(([lng, lat]) => ({ lat, lng })));
				}
			}
		} catch {
			continue;
		}
	}

	return results;
}

/**
 * Get all coordinates for bounds calculation
 */
export function getAllCoordinatesFromGeometry(geometry: GeoJSONPolygon | MultiPolygon): google.maps.LatLngLiteral[] {
	const coords: google.maps.LatLngLiteral[] = [];
	if (geometry.type === "Polygon") {
		geometry.coordinates[0].forEach((coord) => {
			coords.push({ lat: coord[1], lng: coord[0] });
		});
	} else if (geometry.type === "MultiPolygon") {
		geometry.coordinates.forEach((polygon) => {
			polygon[0].forEach((coord) => {
				coords.push({ lat: coord[1], lng: coord[0] });
			});
		});
	}
	return coords;
}
